import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Product, StoreVariantStock, Variant } from '@nucleus-commerce/api-client/admin';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb } from '@/features/media';
import { VariantStorePrices } from '@/features/store-catalog';
import { session } from '@/shared/session/session';

import { useDefaultWeightUnit } from '../hooks/use-default-weight-unit';
import { useVariantImage } from '../hooks/use-variant-image';

import {
  FulfillmentCell,
  MarginFigure,
  MoneyCell,
  NumbersCell,
  StatusCell,
  SwitchCell,
  TextCell,
  WeightCell,
} from './variant-table-cells';
import {
  VariantDetailField,
  VariantDetailGrid,
  VariantDetailGroup,
  VariantDetailSwitch,
  VariantDetailValue,
} from './variants-card-parts';

function ImagePicker({ product, variant }: { product: Product; variant: Variant }) {
  const { t } = useTranslation('products');
  const message = useErrorMessage();
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const { items, current, pick, isLoading, save } = useVariantImage(product, variant);
  return (
    <VariantDetailGroup title={t('variantsCard.groups.image')}>
      {items.length === 0 && !isLoading ? (
        <p className="text-muted-foreground text-[13px]">{t('variantPage.image.empty')}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.mediaId}
              type="button"
              aria-label={t('variantsCard.useImage', { n: index + 1 })}
              aria-pressed={current === item.mediaId}
              disabled={!canWrite || save.isPending}
              // Pressing the image in use takes it off the variant.
              onClick={() => pick(current === item.mediaId ? null : item.mediaId)}
              className={cn(
                'size-11 overflow-hidden rounded-lg border-2 disabled:cursor-default',
                current === item.mediaId ? 'border-primary' : 'border-border',
              )}
            >
              <MediaThumb media={item.media} className="size-full" />
            </button>
          ))}
          <span className="text-muted-foreground text-xs">{t('variantsCard.imageHint')}</span>
        </div>
      )}
      {save.isError ? (
        <p role="alert" className="text-destructive text-xs">
          {message(save.error)}
        </p>
      ) : null}
    </VariantDetailGroup>
  );
}

export interface VariantFieldGroupsProps {
  product: Product;
  variant: Variant;
  /** Stock by store, on the master page; a store shows its own stock instead. */
  stockByStore?: ReactNode;
  /**
   * On a store's page: the master SKU joins these, since the row's SKU is the store's; compare-at
   * and margin stay out, since the store has its own.
   */
  shared?: boolean;
}

/** The master fields of a variant, in the groups both product pages show. Drafts of the row form. */
export function VariantFieldGroups({
  product,
  variant,
  stockByStore,
  shared = false,
}: VariantFieldGroupsProps) {
  const { t } = useTranslation('products');
  const defaultUnit = useDefaultWeightUnit();
  const id = variant.id;
  const currency = variant.price.currency;
  const f = (key: string) => t(`variantsCard.fields.${key}`);
  const label = (field: string) => t('variants.cellFor', { field, variant: variant.title });

  return (
    <>
      <VariantDetailGroup title={t('variantsCard.groups.pricing')}>
        <VariantDetailGrid>
          {shared ? null : (
            <VariantDetailField label={f('compareAt')}>
              <MoneyCell
                id={id}
                field="compareAt"
                currency={currency}
                label={t('variants.columns.compareAtFor', { variant: variant.title })}
              />
            </VariantDetailField>
          )}
          <VariantDetailField label={f('cost')}>
            <MoneyCell id={id} field="cost" currency={currency} label={label(f('cost'))} />
          </VariantDetailField>
          {shared ? null : (
            <>
              <VariantDetailValue label={f('margin')}>
                <MarginFigure id={id} currency={currency} show="margin" />
              </VariantDetailValue>
              <VariantDetailValue label={f('profit')}>
                <MarginFigure id={id} currency={currency} show="profit" />
              </VariantDetailValue>
            </>
          )}
          <VariantDetailField label={f('taxCode')}>
            <TextCell id={id} field="taxCode" label={label(f('taxCode'))} />
          </VariantDetailField>
          <VariantDetailSwitch label={f('taxable')}>
            <SwitchCell id={id} field="taxable" label={label(f('taxable'))} />
          </VariantDetailSwitch>
        </VariantDetailGrid>
      </VariantDetailGroup>

      <VariantDetailGroup title={t('variantsCard.groups.inventory')}>
        <VariantDetailGrid>
          <VariantDetailField label={f('status')}>
            <StatusCell
              id={id}
              label={t('variants.columns.statusFor', { variant: variant.title })}
            />
          </VariantDetailField>
          <VariantDetailField label={f('barcode')}>
            <TextCell id={id} field="barcode" label={label(f('barcode'))} className="font-mono" />
          </VariantDetailField>
          {shared ? (
            <VariantDetailField label={f('masterSku')} className="col-span-2">
              <TextCell
                id={id}
                field="sku"
                label={t('variants.columns.skuFor', { variant: variant.title })}
                className="font-mono"
              />
            </VariantDetailField>
          ) : null}
          {stockByStore === undefined ? null : (
            <VariantDetailValue label={f('stockByStore')} className="col-span-2">
              {stockByStore}
            </VariantDetailValue>
          )}
          <VariantDetailSwitch label={f('track')}>
            <SwitchCell id={id} field="trackInventory" label={label(f('track'))} />
          </VariantDetailSwitch>
          <VariantDetailSwitch label={f('backorder')}>
            <SwitchCell
              id={id}
              field="allowBackorder"
              label={label(f('backorder'))}
              disabledWhen="trackInventory"
            />
          </VariantDetailSwitch>
        </VariantDetailGrid>
      </VariantDetailGroup>

      <VariantDetailGroup title={t('variantsCard.groups.shipping')}>
        <VariantDetailGrid>
          <VariantDetailField label={f('weight')} className="col-span-2">
            <WeightCell id={id} label={label(f('weight'))} defaultUnit={defaultUnit} />
          </VariantDetailField>
          <VariantDetailField label={f('size')} className="col-span-2">
            <NumbersCell
              id={id}
              separator="×"
              fields={(['lengthMm', 'widthMm', 'heightMm'] as const).map((field) => ({
                field,
                label: label(t(`variants.sheet.${field}`)),
              }))}
            />
          </VariantDetailField>
          <VariantDetailSwitch label={f('ships')}>
            <SwitchCell id={id} field="requiresShipping" label={label(f('ships'))} />
          </VariantDetailSwitch>
        </VariantDetailGrid>
      </VariantDetailGroup>

      <VariantDetailGroup title={t('variantsCard.groups.making')}>
        <VariantDetailGrid>
          <VariantDetailField label={f('fulfilment')} className="col-span-2">
            <FulfillmentCell id={id} label={label(f('fulfilment'))} />
          </VariantDetailField>
          <VariantDetailField label={f('daysFrom')}>
            <NumbersCell
              id={id}
              separator=""
              fields={[{ field: 'leadTimeDaysMin', label: label(f('daysFrom')) }]}
            />
          </VariantDetailField>
          <VariantDetailField label={f('daysTo')}>
            <NumbersCell
              id={id}
              separator=""
              fields={[{ field: 'leadTimeDaysMax', label: label(f('daysTo')) }]}
            />
          </VariantDetailField>
        </VariantDetailGrid>
      </VariantDetailGroup>

      <ImagePicker product={product} variant={variant} />
    </>
  );
}

export interface MasterVariantDetailsProps {
  product: Product;
  variant: Variant;
  /** The product's stock rows; null without inventory.read. */
  stock: readonly StoreVariantStock[] | null;
}

/** A master variant's View more: every field the row leaves out, still drafts of the row form. */
export function MasterVariantDetails({ product, variant, stock }: MasterVariantDetailsProps) {
  const { t } = useTranslation('products');
  const { me } = session.useSession();
  const id = variant.id;

  const byStore =
    stock && variant.trackInventory
      ? stock
          .filter((row) => row.variantId === id)
          .flatMap((row) => {
            const store = me?.stores.find((candidate) => candidate.id === row.storeId);
            return store ? [`${store.name} ${row.available}`] : [];
          })
          .join(' · ')
      : '';

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-x-6 gap-y-[1.125rem] sm:grid-cols-2">
        <VariantFieldGroups product={product} variant={variant} stockByStore={byStore || '—'} />
        <VariantStorePrices productId={product.id} variant={variant} />
      </div>
      <div className="border-primary/20 border-t pt-2.5 text-[13px]">
        <Link
          to="/org/products/$productId/variants/$variantId"
          params={{ productId: product.id, variantId: id }}
          className="text-primary hover:underline"
          aria-label={t('variantsCard.openVariantFor', { variant: variant.title })}
        >
          {t('variantsCard.openVariant')}
        </Link>
      </div>
    </div>
  );
}
